import { useContext, useMemo, useCallback } from 'react';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { totalTime } from 'shared/utils/timesheet';
import { getLeafChildrenData } from 'timeSheet/utils';
import { BreaksRenderer } from 'timeSheet/components/CellRenderers';

const useBreaksColumn = ({
  updateBreak,
  deleteBreak,
  hasActionPermission,
  updateOSBreaks,
  deleteOSBreak,
  allowUpdateExternal = false
}) => {
  const { t } = useContext(ConfigContext);

  const getTotalBreakTime = useCallback((breaktimes) => {
    if (!breaktimes || !Array.isArray(breaktimes)) return 0;
    return breaktimes.reduce((sum, { startTime, endTime }) => {
      if (startTime && endTime) {
        return sum + totalTime(startTime, endTime);
      }
      return sum;
    }, 0);
  }, []);

  const valueGetter = useCallback(
    (params) => {
      if (params.node?.group) {
        const groupItems = getLeafChildrenData(params.node);
        return groupItems.reduce((total, item) => {
          return total + getTotalBreakTime(item?.breaktimes);
        }, 0);
      }
      return getTotalBreakTime(params.data?.breaktimes);
    },
    [getTotalBreakTime]
  );

  const comparator = useCallback((valueA, valueB, nodeA, nodeB) => {
    if (nodeA?.group || nodeB?.group) {
      return 0;
    }
    const a = Number(valueA) || 0;
    const b = Number(valueB) || 0;
    return a - b;
  }, []);

  const cellRenderer = useCallback(
    (params) => {
      if (params.node?.group) return null;

      return BreaksRenderer({
        rowData: params.data,
        columnData: {
          updateBreak,
          deleteBreak,
          updateOSBreaks: allowUpdateExternal ? updateOSBreaks : undefined,
          deleteOSBreak: allowUpdateExternal ? deleteOSBreak : undefined,
          readOnly: !hasActionPermission
        }
      });
    },
    [updateBreak, deleteBreak, updateOSBreaks, deleteOSBreak, hasActionPermission, allowUpdateExternal]
  );

  return useMemo(
    () => ({
      field: 'breaks',
      headerName: t('breaks'),
      width: 100,
      cellClass: 'justify-start',
      initialHide: true,
      sortable: true,
      valueGetter,
      comparator,
      cellRenderer
    }),
    [t, valueGetter, comparator, cellRenderer]
  );
};

export default useBreaksColumn;
